import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { AggregateRiskMetrics } from './types';

interface RiskMetricsDisplayProps {
  riskMetrics: AggregateRiskMetrics | null;
  assetName: string;
}

const RiskMetricsDisplay: React.FC<RiskMetricsDisplayProps> = ({
  riskMetrics,
  assetName
}) => {
  if (!riskMetrics) {
    return (
      <Paper sx={{ p: 2, bgcolor: '#2a3441', color: 'white' }}>
        <Typography variant="body2" sx={{ textAlign: 'center', color: '#888' }}>
          Select simulations to view risk metrics
        </Typography>
      </Paper>
    );
  }

  const getRiskLevelColor = (percentage: number) => {
    if (percentage >= 75) return '#ff5252'; // High risk - red
    if (percentage >= 50) return '#ff9800'; // Medium-high risk - orange
    if (percentage >= 25) return '#ffeb3b'; // Medium risk - yellow
    if (percentage > 0) return '#4caf50'; // Low risk - green
    return '#9e9e9e'; // No risk - gray
  };

  const getRiskLevelText = (percentage: number) => {
    if (percentage >= 75) return 'High Risk';
    if (percentage >= 50) return 'Medium-High Risk';
    if (percentage >= 25) return 'Medium Risk';
    if (percentage > 0) return 'Low Risk';
    return 'No Risk';
  };

  return (
    <Box sx={{ color: 'white' }}>
      {/* Aggregate Summary */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: '#2a3441', color: 'white' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Risk Summary for {assetName}
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>
              Total Simulations Analyzed
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#00d4ad' }}>
              {riskMetrics.totalSimulations}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>
              Total Ignitions Analyzed
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#00d4ad' }}>
              {riskMetrics.totalIgnitions.toLocaleString()}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>
              Ignitions Impacting Asset
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff5252' }}>
              {riskMetrics.totalImpactingIgnitions.toLocaleString()}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>
              Average Impact Rate
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff5252' }}>
                {riskMetrics.averageImpactPercentage.toFixed(1)}%
              </Typography>
              <Chip
                label={getRiskLevelText(riskMetrics.averageImpactPercentage)}
                size="small"
                sx={{
                  bgcolor: getRiskLevelColor(riskMetrics.averageImpactPercentage),
                  color: 'black',
                  fontWeight: 'bold'
                }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Detailed Simulation Results */}
      <Paper sx={{ bgcolor: '#2a3441', color: 'white' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Detailed Results by Simulation
          </Typography>
        </Box>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Simulation</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                  Total Ignitions
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                  Impacting Ignitions
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                  Impact Rate
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Risk Level
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {riskMetrics.simulationMetrics.map((metrics) => (
                <TableRow key={metrics.simulationId}>
                  <TableCell sx={{ color: 'white' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {metrics.simulationName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: 'white' }} align="right">
                    {metrics.totalIgnitions.toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ color: 'white' }} align="right">
                    {metrics.impactingIgnitions.toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ color: 'white' }} align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {metrics.impactPercentage.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={getRiskLevelText(metrics.impactPercentage)}
                      size="small"
                      sx={{
                        bgcolor: getRiskLevelColor(metrics.impactPercentage),
                        color: 'black',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default RiskMetricsDisplay;
